"use client"
import { Fragment, memo, useMemo } from "react"
import { COMPARISON_PLAN_ORDER, METRIC_DETAILS, SUPPLEMENT_SLUGS, SUPPLEMENT_TITLES } from "./metricDetailsData"
import { PLAN_BRANCH_SERIES } from "./planBranchSeries"
import { MetricScenarioChart, metricValueAtYear, pointsValueAtYear, type ComparisonBranch } from "./MetricScenarioChart"
import { MetricBarChart } from "./MetricBarChart"
import { buildScenarioSeriesFromModel } from "./buildModelSeries"
import { usePowerShareSeries } from "./powerShareSeries"
import { useMetricDetails } from "./metricDetailsContext"
import type { BranchKey, MetricId } from "./metricDetailsTypes"

// Scenario-row swatches mirror the chart lines. Plan A = narrow solid then
// dashed; Default = red dashed.
const SwBranch = ({ branch }: { branch: BranchKey }) =>
  branch === "A" ? (
    <svg width="18" height="8" viewBox="0 0 18 8" className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2="8" y2="4" stroke="#111111" strokeWidth="1.5" />
      <line x1="9" y1="4" x2="18" y2="4" stroke="#111111" strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  ) : (
    <svg width="18" height="8" viewBox="0 0 18 8" className="inline-block align-middle" aria-hidden>
      <line x1="0" y1="4" x2="18" y2="4" stroke="var(--race-background)" strokeWidth="1.5" strokeDasharray="4 3" />
    </svg>
  )

const ScenarioRow = ({ swatch, name, nameClass, children }: { swatch: React.ReactNode; name: string; nameClass: string; children: React.ReactNode }) => (
  <li className="grid grid-cols-[22px_1fr] items-baseline gap-x-2.5 gap-y-0.5">
    <span className="mt-1 justify-self-start" aria-hidden>{swatch}</span>
    <b className={`font-mono text-[10px] font-bold uppercase tracking-wider ${nameClass}`}>{name}</b>
    {/* Full-width below the swatch+name row — not indented to the name column. */}
    <span className="col-start-1 col-span-2 font-et-book text-[14.5px] leading-[1.45] [&_a]:break-words">{children}</span>
  </li>
)

// memo(): the desktop host (MetricDetailsHudState) re-renders on every
// HudContext tick; all props here are stable between year changes, so the
// body should only re-render when the year (or metric) actually moves.
// The body of a metric's details, written in the HUD's own visual idiom —
// mono uppercase labels, hairline rules, page background, no
// enclosing box — one more state of the same instrument panel. Shared by the
// desktop HUD state (MetricDetailsHudState) and the mobile overlay card.
// Everything fades in immediately on open; only the header title waits for
// the flying metric label to land on it.
export const MetricDetailsContent = memo(({
  metricId,
  year,
  onClose,
  withArticleDivider = false,
}: {
  metricId: MetricId
  // Current HUD year (fractional), synced to scroll — drives the chart's
  // now-cursor and the header's live value readout.
  year: number
  // Renders an inline HUD-style close affordance in the header row (the mobile
  // card omits it and pins its own button to the card frame instead).
  onClose?: () => void
  // Desktop HUD state only: a full-height hairline on the article-facing edge,
  // ending above the footer's horizontal rule.
  withArticleDivider?: boolean
}) => {
  const { modelSeries, comparisonPlan } = useMetricDetails()
  const detail = METRIC_DETAILS[metricId]
  const powerSeries = usePowerShareSeries()

  // Power samples the live power-compute data (the same fetches the HUD's
  // bubbles use); the other live-sourced metrics (income, alignment,
  // safetyTax, employment, labor) come from the server-parsed model series;
  // anything left falls back to the metric's own bundled series. Memoized so
  // the chart's memo() sees a stable series between year ticks.
  const series = useMemo(() => {
    if (!detail) return null
    if (metricId === "power") return powerSeries
    const model = modelSeries[metricId]
    return model && model.planA.length > 0
      ? buildScenarioSeriesFromModel(model, {
          firstYear: detail.xTicks[0],
          observedEndYear: detail.xTicks[0] + 1,
          splitYear: detail.splitYear,
          lastYear: detail.xTicks[detail.xTicks.length - 1],
        })
      : detail.series
  }, [detail, metricId, modelSeries, powerSeries])

  // Every comparison branch drawn against Plan A comes from the model
  // spreadsheet's per-plan series (planBranchSeries), anchored to the shared
  // fork point where a plan's series starts after the split. A plan with no
  // series for this metric simply draws no line — nothing off-spreadsheet is
  // charted.
  const comparisonBranches = useMemo<ComparisonBranch[] | null>(() => {
    if (!series || !detail) return null
    const forkPoint = series.branches.A?.[0]
    return COMPARISON_PLAN_ORDER.map((id) => {
      const modeled = PLAN_BRANCH_SERIES[metricId]?.[id] ?? []
      const needsAnchor = forkPoint && modeled[0] && modeled[0].year > detail.splitYear + 0.01
      return { id, points: needsAnchor ? [forkPoint, ...modeled] : modeled }
    })
  }, [series, detail, metricId])

  if (!detail || !series || !comparisonBranches) return null

  // While the reader is in a non-A branch, the now-dot and header readout ride
  // that plan's line through its full series (see usePlaybookState's
  // metricComparisonPlan); a plan with no series for this metric holds at the
  // fork — there is no line to ride.
  const activePoints = comparisonPlan
    ? comparisonBranches.find((b) => b.id === comparisonPlan)?.points ?? []
    : []
  const dotYear = comparisonPlan
    ? Math.min(year, activePoints.length > 0 ? activePoints[activePoints.length - 1].year : detail.splitYear)
    : year

  const supplementSources = detail.sources.filter((s) => SUPPLEMENT_SLUGS.has(s))
  const nowValue = activePoints.length > 0 && dotYear > detail.splitYear
    ? pointsValueAtYear(activePoints, detail.axis, dotYear)
    : metricValueAtYear(series, detail.axis, dotYear)

  return (
    <div className={`text-[#111111] ${withArticleDivider ? "pl-6" : ""}`}>
      {/* Everything above the footer shares the vertical article divider —
          the hairline hugs this block's height, so it ends before the
          footer's horizontal rule rather than running past it. */}
      <div className={withArticleDivider ? "relative" : undefined}>
        {/* Drawn as a border (not a filled 1px div) so it rasterizes exactly
            like the footer's border-t — a background rect at a fractional x
            anti-aliases across two device pixels and reads thicker. */}
        {withArticleDivider && <div className="absolute -left-6 inset-y-0 w-0 border-l border-gray-300" aria-hidden />}

        {/* One-row header: metric name, live readout (tracks the now-cursor as
            the reader scrolls), close. Fades in place (no rise) — the flying
            metric label lands exactly on the title, which it finds live via
            data-metric-details-title (FlyingMetricLabel). */}
        {/* Without an inline close (mobile), the card pins its own ✕ over the
            header's right edge — pad so the live readout clears it. */}
        <div className={`flex animate-details-fade items-baseline justify-between gap-3 [animation-delay:340ms] motion-reduce:animate-none ${onClose ? "" : "pr-10"}`}>
          {/* m-0: tufte.css gives bare h2 a 3rem/2.5rem margin — without the
              reset the title (and the flying label that lands on it) sits ~48px
              below the top of the column. leading-none on every row item: with
              items-baseline, a taller line box (the value span inherits ~1.5)
              would push the title a few px off the flying label's landing spot. */}
          <h2 data-metric-details-title className="m-0 font-mono text-[17px] font-bold leading-none tracking-[0.04em]">{detail.label}</h2>
          <span className="flex items-baseline gap-3">
            {nowValue && (
              <span className="font-mono text-[15px] font-bold leading-none tabular-nums" aria-label={`Current value: ${nowValue}`}>
                {nowValue}
              </span>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close details"
                className="font-mono text-[15px] leading-none text-[#111111] hover:text-[var(--accent)]"
              >
                ✕
              </button>
            )}
          </span>
        </div>

        {/* Chart flush under the header — the line styles explain themselves in
            the scenario rows below; no legend. Safety Tax is cumulative, so it
            shows the total slowdown each plan accrues by the hand-off as a
            bar-per-plan comparison rather than the over-time trajectory. */}
        <div className="mt-2.5 animate-details-in [animation-delay:240ms] motion-reduce:animate-none">
          {metricId === "safetyTax" ? (
            <MetricBarChart
              series={series}
              comparisonBranches={comparisonBranches}
              axis={detail.axis}
              endLabels={detail.endLabels}
            />
          ) : (
            <MetricScenarioChart
              series={series}
              axis={detail.axis}
              endLabels={detail.endLabels}
              splitYear={detail.splitYear}
              year={dotYear}
              comparisonBranches={comparisonBranches}
              activeBranchId={activePoints.length > 0 ? comparisonPlan : null}
            />
          )}
        </div>

        {detail.explanation && (
          <div className="mt-5 animate-details-in font-et-book text-[14.5px] leading-[1.45] [animation-delay:240ms] motion-reduce:animate-none [&_b]:font-bold [&_em]:italic [&_li]:mb-1.5 [&_p]:mt-0 [&_p]:mb-[10px] [&_p:last-child]:mb-0 [&_ul]:mt-0 [&_ul]:mb-[10px] [&_ul]:list-disc [&_ul]:pl-5">
            {detail.explanation}
          </div>
        )}

        {/* Default path first, then Plan A. */}
        <div className="animate-details-in [animation-delay:240ms] motion-reduce:animate-none">
          <ul className="mt-4 grid list-none gap-2.5 p-0">
            <ScenarioRow swatch={<SwBranch branch="D" />} name="Default path" nameClass="text-[var(--race-background)]">{detail.scenarios.D}</ScenarioRow>
            <ScenarioRow swatch={<SwBranch branch="A" />} name="Plan A" nameClass="text-[#111111]">{detail.scenarios.A}</ScenarioRow>
          </ul>
        </div>
      </div>

      {supplementSources.length > 0 && (
        // Black at a small mono size — de-emphasis by size, not gray
        // (design-style-guide.md); links redden on hover like the rest of
        // the HUD's affordances.
        <div className="mt-5 animate-details-in border-t border-gray-300 pt-2 font-mono text-[10px] text-[#111111] [animation-delay:240ms] motion-reduce:animate-none">
          Explained more in{" "}
          {supplementSources.map((slug, i) => (
            <Fragment key={slug}>
              {i > 0 && (i === supplementSources.length - 1 ? " and " : ", ")}
              <a
                href={`/supplements/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#111111] underline underline-offset-2 hover:text-[var(--accent)]"
              >
                {SUPPLEMENT_TITLES[slug] ?? slug}
              </a>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  )
})
MetricDetailsContent.displayName = "MetricDetailsContent"
